import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, Heart, X, Check } from "lucide-react";
import { API, useAuth } from "@/App";

const POLL_MS = 60_000;

const ForumNotificationsBell = () => {
  const { token } = useAuth();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  const fetchCount = async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/forum/notifications/count`, {
        headers, withCredentials: true,
      });
      setUnread(res.data?.unread || 0);
    } catch { /* silent */ }
  };

  const fetchList = async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/forum/notifications?limit=20`, {
        headers, withCredentials: true,
      });
      setItems(res.data?.notifications || []);
    } catch { /* silent */ }
  };

  useEffect(() => {
    fetchCount();
    const id = setInterval(fetchCount, POLL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [token]);

  useEffect(() => {
    if (open) fetchList();
    // eslint-disable-next-line
  }, [open]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const markRead = async (id) => {
    try {
      await axios.post(`${API}/forum/notifications/${id}/read`, null, {
        headers, withCredentials: true,
      });
      setItems((prev) => prev.map((n) => n.notification_id === id ? { ...n, read: true } : n));
      setUnread((u) => Math.max(0, u - 1));
    } catch { /* silent */ }
  };

  const markAll = async () => {
    try {
      await axios.post(`${API}/forum/notifications/read-all`, null, {
        headers, withCredentials: true,
      });
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnread(0);
    } catch { /* silent */ }
  };

  if (!token) return null;

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 rounded-full hover:bg-white/5 transition"
        aria-label="Forum notifications"
        data-testid="forum-notifications-bell"
      >
        <Bell className="w-5 h-5 text-mystic-100" />
        {unread > 0 && (
          <span
            data-testid="forum-notifications-unread-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.97 }}
            transition={{ duration: 0.18 }}
            className="absolute right-0 mt-2 w-80 sm:w-96 max-w-[calc(100vw-2rem)] z-50"
            data-testid="forum-notifications-dropdown"
          >
            <div className="rounded-2xl bg-[#1a0f2e] border border-white/10 shadow-2xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
                <p className="text-white font-medium text-sm">Notifications</p>
                <div className="flex items-center gap-2">
                  {unread > 0 && (
                    <button
                      onClick={markAll}
                      className="text-xs text-pink-300 hover:text-pink-200"
                      data-testid="mark-all-read-btn"
                    >
                      Mark all read
                    </button>
                  )}
                  <button
                    onClick={() => setOpen(false)}
                    className="text-mystic-300 hover:text-white"
                    aria-label="Close"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="max-h-80 overflow-y-auto">
                {items.length === 0 ? (
                  <div className="text-center py-10 px-4">
                    <Bell className="w-8 h-8 text-mystic-400/50 mx-auto mb-2" />
                    <p className="text-mystic-300 text-sm">You're all caught up.</p>
                  </div>
                ) : (
                  <ul>
                    {items.map((n) => (
                      <li
                        key={n.notification_id}
                        data-testid={`notification-${n.notification_id}`}
                        className={`px-4 py-3 border-b border-white/5 last:border-0 cursor-pointer transition ${
                          n.read ? "opacity-60" : "bg-pink-500/5 hover:bg-pink-500/10"
                        }`}
                        onClick={() => !n.read && markRead(n.notification_id)}
                      >
                        <div className="flex items-start gap-3">
                          <div className="shrink-0 w-8 h-8 rounded-full bg-rose-500/15 flex items-center justify-center">
                            <Heart className="w-4 h-4 text-rose-300" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm">
                              <span className="font-medium">{n.from_user_name || "Someone"}</span>
                              <span className="text-mystic-200"> liked your comment</span>
                            </p>
                            {n.snippet && (
                              <p className="text-mystic-300 text-xs mt-1 line-clamp-2 italic">"{n.snippet}"</p>
                            )}
                            <p className="text-mystic-400 text-xs mt-1">
                              {new Date(n.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                            </p>
                          </div>
                          {n.read && <Check className="w-4 h-4 text-emerald-400 shrink-0" />}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ForumNotificationsBell;
